import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_CARD_WIDTH = 400;
const CARD_MARGIN = 40;

export const useDragging = () => {
  const slider: RefObject<HTMLDivElement> = useRef(null);

  const [isDragging, setDragging] = useState(false);
  const [scrollPosition, setScrollPosition] = useState({
    isScrollStart: true,
    isScrollEnd: false,
    isAllItemsInView: false,
  });

  const initialPos = useRef({ left: 0, x: 0 });

  const calculatePosition = (
    scrollLeft: number,
    scrollWidth: number,
    clientWidth: number
  ) => {
    const isAtBeginning = scrollLeft <= 0;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth;
    const isAllInView = scrollWidth - CARD_MARGIN <= clientWidth;

    return {
      isScrollStart: isAtBeginning,
      isScrollEnd: isAtEnd,
      isAllItemsInView: isAllInView,
    };
  };

  const checkScrollPosition = useCallback(() => {
    if (slider.current) {
      const { scrollLeft, scrollWidth, clientWidth } = slider.current;
      setScrollPosition(
        calculatePosition(scrollLeft, scrollWidth, clientWidth)
      );
    }
  }, [setScrollPosition]);

  const mouseUpHandler = useCallback(() => {
    setDragging(false);
    checkScrollPosition();
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler); // skoða
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDragging, checkScrollPosition]);

  const mouseMoveHandler = useCallback(
    (e: MouseEvent) => {
      if (slider.current) {
        // How far the mouse has been moved
        const dx = e.clientX - initialPos.current.x;

        // Scroll the element
        slider.current.scrollLeft = initialPos.current.left - dx;
        checkScrollPosition();
      }
    },
    [checkScrollPosition]
  );

  const mouseDownHandler = useCallback(
    (e: MouseEvent) => {
      if (slider.current) {
        setDragging(true);

        initialPos.current = {
          left: slider.current.scrollLeft,
          // Get the current mouse position
          x: e.clientX,
        };

        document.addEventListener('mousemove', mouseMoveHandler);
        document.addEventListener('mouseup', mouseUpHandler);
      }
    },
    [mouseMoveHandler, mouseUpHandler, setDragging]
  );

  const sliderButton = (isScrollLeft: boolean, cardDocumentId: string) => {
    if (slider.current) {
      // Get the width of the card
      const cardElement = document.getElementById(cardDocumentId);
      const cardWidth = cardElement
        ? cardElement.getBoundingClientRect().width + CARD_MARGIN
        : DEFAULT_CARD_WIDTH;

      const { scrollLeft, scrollWidth, clientWidth } = slider.current;

      // Calculate the intended next scroll position
      const nextScrollLeft =
        scrollLeft + (isScrollLeft ? -cardWidth : cardWidth);

      slider.current.scrollTo({
        top: 0,
        left: nextScrollLeft,
        behavior: 'smooth',
      });

      setScrollPosition(
        calculatePosition(nextScrollLeft, scrollWidth, clientWidth)
      );
    }
  };

  useEffect(() => {
    if (slider.current) {
      slider.current.addEventListener('mousedown', mouseDownHandler);
      checkScrollPosition(); // Initial check for scroll position
    }

    return () => {
      if (slider.current) {
        slider.current.removeEventListener('mousedown', mouseDownHandler);
      }
    };
  }, [mouseDownHandler, checkScrollPosition]);

  useEffect(() => {
    const handleResize = () => {
      checkScrollPosition();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [checkScrollPosition]);

  return {
    slider,
    isDragging,
    scrollPosition,
    sliderButton,
  };
};
